import { Client, ProductGroup } from '@app/core/models/client';
import {
  computeProductGroupBranding,
  computeVerificationFields,
  ComputeBrandingClient,
  ComputeVerificationFieldsClient,
  ProductGroup as FeedApiProductGroup,
  VerificationFieldConfig,
} from '@relaynetwork/rn-node-feed-api-types';

/**
 * Flat, already-gated prop bag ready to spread onto `<rn-verification-preview>`.
 * Every gating decision (pswTCS/pswTCMandatory/consentTSCS visibility, verification
 * field merge) is made here via the shared feed-api functions so the three preview
 * call sites (PDF preview, config editor, Spanish translations editor) stay in lockstep
 * with the real branding + verification endpoints instead of re-deriving the logic.
 */
export interface VerificationPreviewProps {
  pswEnabled: boolean;
  pswTCS?: string;
  pswTCMandatory?: boolean;
  consentTSCS?: string;
  fields: VerificationFieldConfig[];
  logoUrl: string;
  color: string;
  productGroupName: string;
  showProductGroup: boolean;
  productGroupDescription: string;
  fdic?: ProductGroup['fdic'];
  showFdic: boolean;
  showBanner: boolean;
  bannerUrl: string;
  bannerAltText: string;
  showLogo: boolean;
  showTitleBar: boolean;
  verificationHeader: string;
  verificationBody: string;
  verificationButtonLabel: string;
  language: string;
  /**
   * Raw es overrides (`i18n.es`) forwarded to the preview component, which
   * registers them as the DS i18next `es` bundle so the `Verification` organism
   * resolves `translations.onboarding.verification.*` / `translations.password.*`
   * itself. That's why verificationHeader/body/buttonLabel (and pswTCS/consentTSCS)
   * are passed as the *base* (English) values here, not es-resolved — i18next
   * applies the es override, or falls back to the base, at render time.
   */
  i18nEs?: NonNullable<ProductGroup['i18n']>['es'];
}

/**
 * Per-field Spanish→English fallback mirroring the real app's i18next
 * `fallbackLng: 'en'` behavior: use the Spanish string only when present and
 * non-empty, otherwise fall back to the base (English) value. A partially
 * translated product group therefore renders a mix, not all-or-nothing.
 *
 * Retained only for the verification field prompts: the DS `Verification`
 * organism renders `fields[].prompt` verbatim (there is no
 * `translations.onboarding.verification.fields.*` i18next lookup inside it), so
 * these must still be resolved caller-side. Everything else on this page is now
 * resolved by the DS via the registered es bundle (see `i18nEs`).
 */
function esFallback(
  language: string,
  esValue: string | undefined,
  baseValue: string | undefined,
): string {
  if (language === 'es' && esValue != null && esValue.trim() !== '') {
    return esValue;
  }
  return baseValue ?? '';
}

export function resolveVerificationPreviewProps(
  client: Client,
  productGroup: ProductGroup,
  language: string = 'en',
): VerificationPreviewProps {
  // Portal's Angular models are structurally similar to but not identical to the
  // feed-api TypeBox types (e.g. `verification.enabled` is a `'true'`/`'false'`
  // string here, `VerificationField` carries an extra `labels`, `field` is an enum
  // rather than a string-literal union). Bridge only at this boundary: build the
  // minimal client shape explicitly, and pass the product group through a single
  // structural cast since it satisfies both functions' PG contracts at runtime.
  const feedProductGroup = productGroup as unknown as FeedApiProductGroup;

  const brandingClient: ComputeBrandingClient = {
    verification: { enabled: client.verification?.enabled === 'true' },
    fdic_enabled: client.fdic_enabled,
    two_way_config: {
      translations_enabled: client.two_way_config?.translations_enabled,
    },
  };

  const branding = computeProductGroupBranding(brandingClient, feedProductGroup);

  const verificationFieldsClient: ComputeVerificationFieldsClient = {
    verification: {
      fields: client.verification
        ?.fields as unknown as VerificationFieldConfig[],
    },
  };
  const mergedFields = computeVerificationFields(
    verificationFieldsClient,
    feedProductGroup,
  );

  const esVerification =
    productGroup.i18n?.es?.translations?.onboarding?.verification;
  const esFields = esVerification?.fields;

  const fields: VerificationFieldConfig[] = mergedFields.map((field) => ({
    field: field.field,
    prompt: esFallback(
      language,
      esFields?.[field.field as keyof typeof esFields],
      field.prompt,
    ),
  }));

  // When password is enabled, its T&Cs/consent belong on the password page
  // (not shown in this preview), not the verification page.
  const pswEnabled = branding.pswEnabled ?? false;

  return {
    pswEnabled,
    pswTCS: pswEnabled ? undefined : branding.pswTCS,
    pswTCMandatory: pswEnabled ? undefined : branding.pswTCMandatory,
    consentTSCS: pswEnabled ? undefined : branding.consentTSCS,
    fields,
    logoUrl: productGroup.branding.icon_s3_url,
    color: branding.color,
    productGroupName: productGroup.name,
    showProductGroup: !branding.hideDisplayName,
    productGroupDescription: branding.description ?? productGroup.description,
    fdic: productGroup.fdic,
    showFdic: productGroup.fdic?.show_signage_on_authentication ?? false,
    showBanner: branding.showBanner ?? true,
    // `logoUrl` above is sourced from the portal's own hostnamed model, not
    // `branding.iconUrl` (feed-api's relative-prefixed value) — bannerUrl
    // needs the same treatment for the same reason.
    bannerUrl: productGroup.branding.banner_s3_url,
    bannerAltText: branding.bannerAltText,
    showLogo: branding.showLogo ?? true,
    showTitleBar: branding.showTitleBar ?? true,
    // Base (English) values — the DS `Verification` organism resolves the es
    // override itself from the registered `i18nEs` bundle via
    // `t('translations.onboarding.verification.*', <base>)`.
    verificationHeader: branding.verificationHeader ?? '',
    verificationBody: branding.verificationBody ?? '',
    verificationButtonLabel: branding.verificationButtonLabel ?? '',
    language,
    i18nEs: productGroup.i18n?.es,
  };
}
